import React, {useMemo, useState, useEffect} from 'react'
import {useSelector} from 'react-redux'
import {
  selectKP11SelectCartCentersNApps,
  selectThryvSubscriptionResponse,
} from '../../../../../../../app-store/kp11CartSlice'
import {
  CardPlanBodyStyle,
  CardPlanAddToCartStyle,
} from '../../kp-11Link-catalog-cards-style'
import {kpSuccessIcon} from '../../../../../../../assets'
import {PlanRadioInput} from '../../../../kp-11Link-reusable-components/kp-plan-radio-input'
import {CardPlanItemWrapStyle} from '../../../../kp-11Link-reusable-components/kp-plan-radio-input-style'
import {CardPlanEntitlementsStyle} from '../../../../kp-11Link-reusable-components/kp-plan-ent-checkbox-style'
import {EntCheckboxInput} from '../../../../kp-11Link-reusable-components/kp-plan-ent-checkbox'
import {DELAY_HIDE_IN_SEC} from '../../../../../../../utils/constants'
import {
  buildCartEntity,
  countryCurrencyEnum,
  getItemFromCart,
  isOneTimeItem,
} from '../../../../kp-11Link-utility/kp11-utility'
import {GENERIC_CARD_LIST} from '../../../../../../../utils/constants'
import {RegularCardComponent} from '../../../../../kp-card-type/regular-card'
import KP11KeapAdditionalContacts from './kp11-keap-additional-contacts'
import KP11KeapAdditionalUsers from './kp11-keap-additional-users'
import KP11KeapTextVoice from './kp11-keap-text-voice'
import KP11KeapCustomDoneForYou from './kp11-keap-custom-done-for-you'
import KP11KeapHourlyDataService from './kp11-keap-hourly-data-service'
import KP11KeapKcpCertification from './kp11-keap-kcp-certification'
import KP11KeapLegacyGraphlyContacts from './kp11-keap-legacy-graphly-contacts'
import KP11KeapLegacyPlusThisPremium from './kp11-keap-legacy-plusthis-premium'

const getProductPrice = (prices, country = 'US') => {
  const currency = countryCurrencyEnum[country] || ''
  const price = prices?.find(p => p.currency === currency)
  return price ? price.unitAmount : null
}

const resolveBillingType = item => {
  if (item?.productBillingType === 'Annual') return 'Annual'
  if (item?.productBillingType === 'Monthly') return 'Monthly'
  if (String(item?.code || '').includes('-annual')) return 'Annual'
  return 'Monthly'
}

// Maps add-on product codes to the prerequisite Recurly subscription codes required for them to be visible.
// If an account does not have at least one of the listed prerequisite codes in their active subscriptions,
// the add-on will be hidden from the catalog. To gate a new product, add an entry here — no logic changes needed.
const REQUIRED_EXISTING_SUBSCRIPTIONS_TO_SHOW_ADDON = {
  'addon-5-keap-legacy-graphly-contacts-m2m': [
    'addon-5-keap-legacy-graphly-business-m2m',
    'addon-5-keap-legacy-graphly-jump-m2m',
    'addon-5-keap-legacy-graphly-starter-m2m',
  ],
  'addon-5-keap-legacy-graphly-contacts-annual': [
    'addon-5-keap-legacy-graphly-business-annual',
    'addon-5-keap-legacy-graphly-jump-annual',
    'addon-5-keap-legacy-graphly-starter-annual',
  ],
  'addon-5-keap-legacy-plusthis-premium-trans-m2m': [
    'addon-5-keap-legacy-plusthis-premium-m2m',
  ],
  'addon-5-keap-legacy-plusthis-premium-trans-annual': [
    'addon-5-keap-legacy-plusthis-premium-annual',
  ],
}

const getExistingSubscriptionCodes = response => {
  if (!response) return new Set()
  const codes = new Set()
  const collectFrom = subs => {
    if (!Array.isArray(subs)) return
    subs.forEach(sub => {
      if (sub?.code) codes.add(sub.code)
      ;(sub?.addons || []).forEach(addon => {
        if (addon?.code) codes.add(addon.code)
      })
    })
  }
  collectFrom(response.childSubscriptions)
  collectFrom(response.parentSubscriptions)
  return codes
}

const getSelectedKeapBillingType = cartCentersAndApps => {
  const selectedKeapItem = [
    ...(cartCentersAndApps?.centers || []),
    ...(cartCentersAndApps?.apps || []),
  ].find(item => String(item?.code || '').includes('keap_'))

  return resolveBillingType(selectedKeapItem)
}

// Catalog card component
export const KP11KeapAddonsCardComponent = ({
  selectedCountry,
  groupKey,
  items,
  onSelectItem,
  disabled,
  addedToCart,
  onGroupCustomFieldsUpdate,
  groupCustomFields = {},
}) => {
  // Get selectedItem from Redux instead of props
  const cartCentersAndApps = useSelector(selectKP11SelectCartCentersNApps)
  const thryvSubscriptionResponse = useSelector(selectThryvSubscriptionResponse)
  const centers = cartCentersAndApps?.centers?.map(item => item.code) || []
  const apps = cartCentersAndApps?.apps?.map(item => item.code) || []
  const oneTimeFee =
    cartCentersAndApps?.oneTimeFees?.map(item => item.code) || []
  const codeList = [...centers, ...apps, ...oneTimeFee]
  const selectedBillingType = getSelectedKeapBillingType(cartCentersAndApps)
  const selectedItem = getItemFromCart({
    cartCentersAndApps,
    groupKey,
    items,
  })
  const visibleItems = useMemo(() => {
    const existingCodes = getExistingSubscriptionCodes(
      thryvSubscriptionResponse,
    )
    return items.filter(item => {
      if (isOneTimeItem(item?.code)) return true
      const billingType = resolveBillingType(item)
      if (selectedBillingType === 'Annual') {
        if (billingType !== 'Annual') return false
      } else {
        if (billingType === 'Annual') return false
      }
      const requiredCodes =
        REQUIRED_EXISTING_SUBSCRIPTIONS_TO_SHOW_ADDON[item?.code]
      if (requiredCodes) {
        return requiredCodes.some(code => existingCodes.has(code))
      }
      return true
    })
  }, [items, selectedBillingType, thryvSubscriptionResponse])
  const availableAddons =
    (selectedItem &&
      visibleItems.find(i => i.id === selectedItem.id)?.entitlements) ||
    []
  //   const dispatch = useDispatch()
  const [add2CartBannerStatus, setAdd2BannerStatus] = useState(addedToCart)
  useEffect(() => {
    if (addedToCart) {
      setAdd2BannerStatus(true)
      setTimeout(() => {
        setAdd2BannerStatus(false)
      }, DELAY_HIDE_IN_SEC * 1000)
    }
  }, [addedToCart])
  return (
    <>
      <CardPlanBodyStyle>
        {/* <div className="card-plan-subText">Select a Keap Addons plan</div> */}
        <CardPlanItemWrapStyle wrap={true} column={true}>
          {visibleItems.map(item => {
            const billingType = `Billed ${resolveBillingType(item)}`
            if (GENERIC_CARD_LIST.includes(item.code)) {
              return (
                <RegularCardComponent
                  key={item.id}
                  groupKey={groupKey}
                  isChecked={
                    selectedItem?.code === item.code ||
                    codeList.includes(item.code)
                  }
                  handleCardAction={() => {
                    if (!codeList.includes(item.code)) {
                      const updatedItem = buildCartEntity({
                        kind: 'item',
                        source: {
                          ...item,
                          groupKey,
                        },
                      })
                      onSelectItem(updatedItem)
                    }
                  }}
                  isDisabled={disabled}
                  country={selectedCountry}
                  cardDetails={{...item, billingType}}
                />
              )
            } else if (String(item.code).includes('addon-5-keap-addcontacts')) {
              return (
                <KP11KeapAdditionalContacts
                  key={item.id}
                  groupKey={groupKey}
                  itemCode={item.code}
                  item={item}
                  name={item.name}
                  selectedCountry={selectedCountry}
                  section={items[0]?.section}
                  isChecked={
                    selectedItem?.code === item.code ||
                    codeList.includes(item.code)
                  }
                  onAddToCart={(updatedItem, value) => {
                    onSelectItem(updatedItem)
                    onGroupCustomFieldsUpdate({
                      keapAdditionalContactsValue: value,
                    })
                  }}
                  price={getProductPrice(item?.currencies, selectedCountry)}
                  selectedOption={
                    groupCustomFields?.keapAdditionalContactsValue
                  }
                  billingType={billingType}
                />
              )
            } else if (String(item.code).includes('addon-5-keap-addusers')) {
              const existingAddUsersApp = (cartCentersAndApps?.apps || []).find(
                app => String(app.code).includes('addon-5-keap-addusers'),
              )
              return (
                <KP11KeapAdditionalUsers
                  key={item.id}
                  isChecked={
                    selectedItem?.code === item.code ||
                    codeList.includes(item.code)
                  }
                  price={getProductPrice(item?.currencies, selectedCountry)}
                  groupKey={groupKey}
                  itemCode={item.code}
                  onSave={finalValue => {
                    const existingApp = existingAddUsersApp
                    const existingQty = Number(existingApp?.quantity ?? 0)
                    const finalQty = Number(finalValue)
                    // Block downgrade: never allow quantity below existing
                    if (existingQty > 0 && finalQty < existingQty) return
                    // Skip if selecting same quantity as existing (prevents unnecessary replace in Order Summary)
                    if (
                      existingApp?.isAlreadyAdded &&
                      finalQty === existingQty
                    ) {
                      onGroupCustomFieldsUpdate({
                        keapAdditionalUsersValue: finalValue,
                      })
                      return
                    }
                    const updatedItem = buildCartEntity({
                      source: {
                        ...item,
                        groupKey,
                      },
                      quantity: finalValue,
                    })
                    onSelectItem(updatedItem)
                    onGroupCustomFieldsUpdate({
                      keapAdditionalUsersValue: finalValue,
                    })
                  }}
                  quantity={
                    groupCustomFields?.keapAdditionalUsersValue ??
                    existingAddUsersApp?.quantity
                  }
                  billingType={billingType}
                  selectedCountry={selectedCountry}
                />
              )
            } else if (String(item.code).includes('addon-5-keap-textvoice')) {
              return (
                <KP11KeapTextVoice
                  key={item.id}
                  groupKey={groupKey}
                  itemCode={item.code}
                  item={item}
                  name={item.name}
                  selectedCountry={selectedCountry}
                  section={items[0]?.section}
                  isChecked={
                    selectedItem?.code === item.code ||
                    codeList.includes(item.code)
                  }
                  onAddToCart={(updatedItem, value) => {
                    onSelectItem(updatedItem)
                    onGroupCustomFieldsUpdate({
                      keapTextVoiceValue: value,
                    })
                  }}
                  price={getProductPrice(item?.currencies, selectedCountry)}
                  selectedOption={groupCustomFields?.keapTextVoiceValue}
                  billingType={billingType}
                />
              )
            } else if (String(item.code).includes('addon-5-keap-custdfy')) {
              return (
                <KP11KeapCustomDoneForYou
                  key={item.id}
                  isChecked={
                    selectedItem?.code === item.code ||
                    codeList.includes(item.code)
                  }
                  price={getProductPrice(item?.currencies, selectedCountry)}
                  groupKey={groupKey}
                  itemCode={item.code}
                  onSave={finalValue => {
                    const updatedItem = buildCartEntity({
                      source: {
                        ...item,
                        groupKey,
                      },
                      quantity: finalValue,
                    })
                    onSelectItem(updatedItem)
                    onGroupCustomFieldsUpdate({
                      keapCustomDoneForYouValue: finalValue,
                    })
                  }}
                  quantity={groupCustomFields?.keapCustomDoneForYouValue}
                  billingType={billingType}
                  selectedCountry={selectedCountry}
                />
              )
            } else if (
              String(item.code).includes('addon-5-keap-hourlydatasvc')
            ) {
              return (
                <KP11KeapHourlyDataService
                  key={item.id}
                  isChecked={
                    selectedItem?.code === item.code ||
                    codeList.includes(item.code)
                  }
                  price={getProductPrice(item?.currencies, selectedCountry)}
                  groupKey={groupKey}
                  itemCode={item.code}
                  onSave={finalValue => {
                    const updatedItem = buildCartEntity({
                      source: {
                        ...item,
                        groupKey,
                      },
                      quantity: finalValue,
                    })
                    onGroupCustomFieldsUpdate({
                      keapHourlyDataServiceValue: finalValue,
                    })
                    onSelectItem(updatedItem)
                  }}
                  quantity={groupCustomFields?.keapHourlyDataServiceValue}
                  billingType={billingType}
                  selectedCountry={selectedCountry}
                />
              )
            } else if (
              String(item.code).includes('addon-5-keap-legacy-graphly')
            ) {
              return (
                <KP11KeapLegacyGraphlyContacts
                  key={item.id}
                  groupKey={groupKey}
                  itemCode={item.code}
                  item={item}
                  name={item.name}
                  selectedCountry={selectedCountry}
                  section={items[0]?.section}
                  isChecked={
                    selectedItem?.code === item.code ||
                    codeList.includes(item.code)
                  }
                  onAddToCart={(updatedItem, value) => {
                    onSelectItem(updatedItem)
                    onGroupCustomFieldsUpdate({
                      keapLegacyGraphlyContactsValue: value,
                    })
                  }}
                  price={getProductPrice(item?.currencies, selectedCountry)}
                  selectedOption={
                    groupCustomFields?.keapLegacyGraphlyContactsValue
                  }
                  billingType={billingType}
                />
              )
            } else if (
              String(item.code).includes('addon-5-keap-legacy-plusthis')
            ) {
              return (
                <KP11KeapLegacyPlusThisPremium
                  key={item.id}
                  groupKey={groupKey}
                  itemCode={item.code}
                  item={item}
                  name={item.name}
                  selectedCountry={selectedCountry}
                  section={items[0]?.section}
                  isChecked={
                    selectedItem?.code === item.code ||
                    codeList.includes(item.code)
                  }
                  onAddToCart={(updatedItem, value) => {
                    onSelectItem(updatedItem)
                    onGroupCustomFieldsUpdate({
                      keapLegacyPlusThisPremiumValue: value,
                    })
                  }}
                  price={getProductPrice(item?.currencies, selectedCountry)}
                  selectedOption={
                    groupCustomFields?.keapLegacyPlusThisPremiumValue
                  }
                  billingType={billingType}
                />
              )
            } else if (String(item.code).includes('keap_kcp-cert-5-fee')) {
              return (
                <KP11KeapKcpCertification
                  key={item.id}
                  groupKey={groupKey}
                  itemCode={item.code}
                  item={item}
                  name={item.name}
                  selectedCountry={selectedCountry}
                  section={items[0]?.section}
                  isChecked={
                    selectedItem?.code === item.code ||
                    codeList.includes(item.code)
                  }
                  onAddToCart={(updatedItem, value) => {
                    onSelectItem(updatedItem)
                    onGroupCustomFieldsUpdate({
                      keapKcpValue: value,
                    })
                  }}
                  price={getProductPrice(item?.currencies, selectedCountry)}
                  selectedOption={groupCustomFields?.keapKcpValue}
                  billingType={billingType}
                />
              )
            } else {
              return (
                <>
                  <PlanRadioInput
                    key={item.id}
                    checked={
                      selectedItem?.code === item.code ||
                      codeList.includes(item.code)
                    }
                    onChange={() => {
                      const updatedItem = buildCartEntity({
                        kind: 'item',
                        source: {
                          ...item,
                          groupKey,
                        },
                      })
                      onSelectItem(updatedItem)
                    }}
                    item={item}
                    disabled={
                      item.code === 'keap_kcc-cert-5-fee'
                        ? codeList.includes('keap_kcp-cert-5-fee')
                          ? true
                          : false
                        : disabled
                    }
                    billingType={billingType}
                    country={selectedCountry}
                  />
                </>
              )
            }
          })}
        </CardPlanItemWrapStyle>

        {/* Addons (entitlements) in checkbox */}
        {availableAddons.length > 0 && (
          <CardPlanEntitlementsStyle>
            {availableAddons.map(ent => (
              <EntCheckboxInput
                key={ent.id}
                checked={
                  selectedItem?.addons?.some(a => a.code === ent.code) || false
                }
                label={ent.name}
                disabled={disabled}
              />
            ))}
          </CardPlanEntitlementsStyle>
        )}
        {add2CartBannerStatus && (
          <CardPlanAddToCartStyle>
            <img src={kpSuccessIcon} alt="success icon" />
            <span>Added to cart</span>
          </CardPlanAddToCartStyle>
        )}
      </CardPlanBodyStyle>
    </>
  )
}
